/* =========================================================
   NAPSHIRTS ONESIE: landing page behavior
   - size pills + per-size hint
   - "laundry rotation" add-on toggles (color + size each)
   - live free-shipping meter (cart subtotal + current selection)
   - multi-item add to cart, wired into Impact's native cart drawer
   - sticky mobile add-to-cart bar
   - cart drawer rotation upsell (delegated, survives drawer re-renders)
   No dependencies. Runs alongside Impact's theme.js.
   ========================================================= */
(function () {
  'use strict';

  if (window.__wbcNapshirtsLoaded) return;
  window.__wbcNapshirtsLoaded = true;

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function money(cents) {
    var n = Math.round(cents) / 100;
    var s = n.toFixed(n % 1 === 0 ? 0 : 2);
    return '$' + s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  /* ---------------------------------------------------------
     Cart: add one or more variants, then hand off to Impact
     exactly the way its own product-form does.
     --------------------------------------------------------- */
  function addToCart(items) {
    var sections = ['variant-added'];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sections } })
    );

    return fetch(ROOT + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        items: items,
        sections: sections.join(','),
        sections_url: ROOT + 'variants/' + items[0].id
      })
    }).then(function (response) {
      return response.json().then(function (json) {
        if (!response.ok) {
          var err = new Error(json.description || json.message || 'Could not add to cart.');
          err.payload = json;
          throw err;
        }

        var settings = (window.themeVariables && window.themeVariables.settings) || {};
        if (settings.cartType === 'page' || settings.pageType === 'cart') {
          window.location.href = ROOT + 'cart';
          return json;
        }

        return fetch(ROOT + 'cart.js')
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            cart.sections = json.sections;

            /* This store's cart type is "popover", so Impact answers a
               `variant:add` event by opening the small notification popover.
               The rotation upsell lives in the cart drawer, so this page skips
               that event, re-renders the drawer through `cart:change`, and
               opens the drawer itself. The rest of the store keeps the popover.
               Shopify's own web pixel still records the add from /cart/add.js,
               so nothing is lost for ad tracking. */
            document.documentElement.dispatchEvent(
              new CustomEvent('cart:change', {
                bubbles: true,
                detail: { baseEvent: 'variant:add', cart: cart }
              })
            );

            var drawer = document.getElementById('cart-drawer');
            if (drawer && typeof drawer.show === 'function') {
              drawer.show();
            }

            return cart;
          });
      });
    });
  }

  /* =========================================================
     BUY BOX
     ========================================================= */
  function initBuyBox(root) {
    var configNode = root.querySelector('[data-nap-config]');
    if (!configNode) return;

    var cfg;
    try { cfg = JSON.parse(configNode.textContent); } catch (e) { return; }

    var state = {
      size: null,
      addons: cfg.addons.map(function (a) {
        return { on: false, color: a.colors[0] || null, size: a.defaultSize || (a.sizes[0] || null) };
      }),
      cartTotal: cfg.cartTotal || 0
    };

    var els = {
      pills: root.querySelectorAll('[data-nap-size]'),
      pillWrap: root.querySelector('[data-nap-size-wrap]'),
      hint: root.querySelector('[data-nap-hint]'),
      helper: root.querySelector('[data-nap-helper]'),
      meter: root.querySelector('[data-nap-meter]'),
      meterLabel: root.querySelector('[data-nap-meter-label]'),
      meterCount: root.querySelector('[data-nap-meter-count]'),
      meterFill: root.querySelector('[data-nap-meter-fill]'),
      addBtn: root.querySelector('[data-nap-add]'),
      addLabel: root.querySelector('[data-nap-add-label]'),
      error: root.querySelector('[data-nap-error]'),
      stickyBar: document.querySelector('[data-nap-sticky]'),
      stickyPrice: document.querySelector('[data-nap-sticky-price]'),
      stickyAdd: document.querySelector('[data-nap-sticky-add]'),
      buySection: document.getElementById('nap-buy')
    };

    /* ---------- variant resolution ---------- */
    function mainVariant() {
      if (!state.size) return null;
      var matches = cfg.main.variants.filter(function (v) {
        return String(v.options[cfg.main.sizeIndex]) === String(state.size);
      });
      if (!matches.length) return null;
      return matches.filter(function (v) { return v.available; })[0] || matches[0];
    }

    function addonVariant(i) {
      var a = cfg.addons[i];
      var st = state.addons[i];
      if (!a || !st) return null;
      var matches = a.variants.filter(function (v) {
        var sizeOk = a.sizeIndex < 0 || String(v.options[a.sizeIndex]) === String(st.size);
        var colorOk = a.colorIndex < 0 || !st.color || String(v.options[a.colorIndex]) === String(st.color);
        return sizeOk && colorOk;
      });
      if (!matches.length) return null;
      return matches.filter(function (v) { return v.available; })[0] || matches[0];
    }

    function addonUnitPrice(i) {
      var a = cfg.addons[i];
      var v = addonVariant(i);
      var base = v ? v.price : a.basePrice;
      return Math.max(0, base - (a.discount || 0));
    }

    function onCount() {
      return state.addons.filter(function (s) { return s.on; }).length;
    }

    function selectionTotal() {
      var mv = mainVariant();
      var total = mv ? mv.price : cfg.main.basePrice;
      state.addons.forEach(function (st, i) { if (st.on) total += addonUnitPrice(i); });
      return total;
    }

    /* ---------- render ---------- */
    function renderSizes() {
      Array.prototype.forEach.call(els.pills, function (btn) {
        var isOn = btn.dataset.napSize === state.size;
        btn.classList.toggle('is-on', isOn);
        btn.setAttribute('aria-checked', isOn ? 'true' : 'false');
      });

      if (els.hint) {
        els.hint.classList.remove('is-error');
        els.hint.textContent = state.size
          ? (state.size + ' · ' + (cfg.hints[state.size] || ''))
          : cfg.strings.sizeDefault;
      }
    }

    function renderAddons() {
      cfg.addons.forEach(function (a, i) {
        var card = root.querySelector('[data-nap-addon="' + i + '"]');
        if (!card) return;
        var st = state.addons[i];

        card.classList.toggle('is-on', st.on);
        var toggle = card.querySelector('[data-nap-addon-toggle]');
        if (toggle) toggle.setAttribute('aria-pressed', st.on ? 'true' : 'false');

        card.querySelectorAll('[data-nap-addon-color]').forEach(function (dot) {
          dot.classList.toggle('is-on', dot.dataset.napAddonColor === st.color);
          dot.setAttribute('aria-checked', dot.dataset.napAddonColor === st.color ? 'true' : 'false');
        });

        var colorLabel = card.querySelector('[data-nap-addon-color-label]');
        if (colorLabel) colorLabel.textContent = st.color || '';

        card.querySelectorAll('[data-nap-addon-size]').forEach(function (pill) {
          var isOn = pill.dataset.napAddonSize === st.size;
          pill.classList.toggle('is-on', isOn);
          pill.setAttribute('aria-checked', isOn ? 'true' : 'false');

          var exists = a.variants.some(function (v) {
            var sizeOk = a.sizeIndex < 0 || String(v.options[a.sizeIndex]) === String(pill.dataset.napAddonSize);
            var colorOk = a.colorIndex < 0 || !st.color || String(v.options[a.colorIndex]) === String(st.color);
            return sizeOk && colorOk && v.available;
          });
          pill.setAttribute('aria-disabled', exists ? 'false' : 'true');
        });

        var priceEl = card.querySelector('[data-nap-addon-price]');
        if (priceEl) priceEl.textContent = money(addonUnitPrice(i));

        var thumb = card.querySelector('[data-nap-addon-thumb]');
        var v = addonVariant(i);
        if (thumb && v && v.image) thumb.setAttribute('src', v.image);
      });

      if (els.helper) {
        var n = onCount();
        var complete = n === cfg.addons.length && cfg.addons.length > 0;
        els.helper.classList.toggle('is-complete', complete);
        els.helper.textContent = complete
          ? cfg.strings.helperAll
          : (n === 0 ? cfg.strings.helperNone : cfg.strings.helperOne);
      }
    }

    /* The meter tracks the real free-shipping threshold only. Completing the
       rotation earns $10 off, not free shipping. Shopify cannot scope a
       free-shipping discount to a specific set of products. */
    function renderMeter() {
      if (!els.meter) return;
      var total = state.cartTotal + selectionTotal();
      var reached = total >= cfg.threshold;
      var pct = reached ? 100 : Math.min(100, Math.round((total / cfg.threshold) * 100));

      els.meter.classList.toggle('is-free', reached);
      if (els.meterFill) els.meterFill.style.width = pct + '%';
      if (els.meterLabel) {
        els.meterLabel.textContent = reached
          ? cfg.strings.meterFree
          : cfg.strings.meterGap.replace('[amount]', money(cfg.threshold - total));
      }
      if (els.meterCount) {
        els.meterCount.textContent = money(total) + ' / ' + money(cfg.threshold);
      }
    }

    function renderCta() {
      var label = cfg.strings.addToCart + '  ·  ' + money(selectionTotal());
      if (els.addLabel) els.addLabel.textContent = label;
      if (els.stickyPrice) els.stickyPrice.textContent = money(selectionTotal());

      var mv = mainVariant();
      var soldOut = state.size && (!mv || !mv.available);
      [els.addBtn, els.stickyAdd].forEach(function (btn) {
        if (!btn) return;
        btn.disabled = !!soldOut;
      });
      if (soldOut && els.addLabel) els.addLabel.textContent = cfg.strings.soldOut;
    }

    function renderAll() {
      renderSizes();
      renderAddons();
      renderMeter();
      renderCta();
    }

    /* ---------- events ---------- */
    Array.prototype.forEach.call(els.pills, function (btn) {
      btn.addEventListener('click', function () {
        if (btn.getAttribute('aria-disabled') === 'true') return;
        state.size = btn.dataset.napSize;
        if (els.error) els.error.textContent = '';
        renderAll();
      });
    });

    cfg.addons.forEach(function (a, i) {
      var card = root.querySelector('[data-nap-addon="' + i + '"]');
      if (!card) return;

      var toggle = card.querySelector('[data-nap-addon-toggle]');
      if (toggle) {
        toggle.addEventListener('click', function () {
          state.addons[i].on = !state.addons[i].on;
          renderAll();
        });
      }

      card.querySelectorAll('[data-nap-addon-color]').forEach(function (dot) {
        dot.addEventListener('click', function (e) {
          e.stopPropagation();
          state.addons[i].color = dot.dataset.napAddonColor;
          renderAll();
        });
      });

      card.querySelectorAll('[data-nap-addon-size]').forEach(function (pill) {
        pill.addEventListener('click', function (e) {
          e.stopPropagation();
          if (pill.getAttribute('aria-disabled') === 'true') return;
          state.addons[i].size = pill.dataset.napAddonSize;
          renderAll();
        });
      });
    });

    function scrollToBuy() {
      if (!els.buySection) return;
      var top = els.buySection.getBoundingClientRect().top + window.scrollY - 78;
      var before = window.scrollY;

      try {
        window.scrollTo({ top: top, behavior: 'smooth' });
      } catch (e) {
        window.scrollTo(0, top);
        return;
      }

      /* This is the page's primary call to action, and on the banner layout it
         is the only thing the artwork's button can do. If smooth scrolling is
         unavailable or blocked, the click would silently do nothing, so check
         that the page actually moved and jump outright if it did not. */
      setTimeout(function () {
        if (Math.abs(window.scrollY - before) < 4 && Math.abs(before - top) > 8) {
          window.scrollTo(0, top);
        }
      }, 350);
    }

    function requireSize() {
      if (state.size) return true;
      scrollToBuy();
      if (els.pillWrap) {
        els.pillWrap.classList.remove('nap-shake');
        void els.pillWrap.offsetWidth;
        els.pillWrap.classList.add('nap-shake');
        setTimeout(function () { els.pillWrap.classList.remove('nap-shake'); }, 500);
      }
      if (els.hint) {
        els.hint.textContent = cfg.strings.pickSize;
        els.hint.classList.add('is-error');
      }
      return false;
    }

    function handleAdd(sourceBtn) {
      if (!requireSize()) return;

      var mv = mainVariant();
      if (!mv) return;

      var items = [{ id: mv.id, quantity: 1 }];
      state.addons.forEach(function (st, i) {
        if (!st.on) return;
        var av = addonVariant(i);
        if (av) items.push({ id: av.id, quantity: 1 });
      });

      if (els.error) els.error.textContent = '';
      [els.addBtn, els.stickyAdd].forEach(function (b) { if (b) b.setAttribute('aria-busy', 'true'); });

      addToCart(items)
        .then(function (cart) {
          if (cart && typeof cart.total_price === 'number') {
            state.cartTotal = cart.total_price;
          }
          state.addons.forEach(function (st) { st.on = false; });
          renderAll();
        })
        .catch(function (err) {
          if (els.error) els.error.textContent = err.message || cfg.strings.addError;
        })
        .then(function () {
          [els.addBtn, els.stickyAdd].forEach(function (b) { if (b) b.removeAttribute('aria-busy'); });
        });
    }

    if (els.addBtn) els.addBtn.addEventListener('click', function () { handleAdd(els.addBtn); });
    if (els.stickyAdd) els.stickyAdd.addEventListener('click', function () { handleAdd(els.stickyAdd); });

    document.querySelectorAll('[data-nap-scroll-to-buy]').forEach(function (link) {
      link.addEventListener('click', function (e) {
        e.preventDefault();
        scrollToBuy();
      });
    });

    /* keep the meter honest if the cart changes elsewhere on the page */
    document.addEventListener('cart:change', function (e) {
      var cart = e.detail && e.detail.cart;
      if (cart && typeof cart.total_price === 'number') {
        state.cartTotal = cart.total_price;
        renderMeter();
      }
    });
    document.addEventListener('cart:refresh', function () {
      fetch(ROOT + 'cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) { state.cartTotal = cart.total_price; renderMeter(); })
        .catch(function () { /* leave the last known total in place */ });
    });

    initGallery(root);
    initSticky(els);
    renderAll();
  }

  /* ---------- gallery ---------- */
  function initGallery(root) {
    var main = root.querySelector('[data-nap-gallery-main]');
    if (!main) return;
    var thumbs = root.querySelectorAll('[data-nap-gallery-thumb]');
    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var img = thumb.querySelector('img');
        if (!img) return;
        main.setAttribute('src', img.dataset.napFull || img.getAttribute('src'));
        main.setAttribute('srcset', '');
        if (img.getAttribute('alt')) main.setAttribute('alt', img.getAttribute('alt'));
        thumbs.forEach(function (t) { t.classList.toggle('is-on', t === thumb); });
      });
    });
  }

  /* ---------- sticky mobile bar ---------- */
  function initSticky(els) {
    var bar = els.stickyBar;
    var anchor = els.addBtn;
    if (!bar || !anchor) return;

    var drawerOpen = false;
    var pastBuyBox = false;

    function apply() {
      bar.setAttribute('data-visible', pastBuyBox && !drawerOpen ? 'true' : 'false');
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          pastBuyBox = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          apply();
        });
      }, { threshold: 0, rootMargin: '0px 0px -40px 0px' }).observe(anchor);
    }

    if (window.matchMedia('(max-width: 960px)').matches && !document.querySelector('.nap-sticky-pad')) {
      var pad = document.createElement('div');
      pad.className = 'nap-sticky-pad';
      var main = document.querySelector('main') || document.body;
      main.appendChild(pad);
    }

    var drawer = document.getElementById('cart-drawer');
    if (drawer && 'MutationObserver' in window) {
      new MutationObserver(function () {
        drawerOpen = drawer.hasAttribute('open');
        apply();
      }).observe(drawer, { attributes: true, attributeFilter: ['open'] });
    }
  }

  /* =========================================================
     STORY TIMELINE: scroll-driven progress rail
     Purely additive. Rows are already fully readable with no JS;
     this only adds a rail that fills as each row crosses the
     reading line, and brightens that row's body text to match the
     hover/focus-within treatment defined in CSS. Skipped entirely
     without IntersectionObserver support, leaving the timeline
     exactly as the static design.
     ========================================================= */
  function initTimeline(root) {
    if (!('IntersectionObserver' in window)) return;

    var fill = root.querySelector('[data-nap-timeline-fill]');
    var rows = Array.prototype.slice.call(root.querySelectorAll('[data-nap-timeline-row]'));
    if (!fill || !rows.length) return;

    root.classList.add('nap-timeline--enhanced');

    var inBand = rows.map(function () { return false; });
    var active = -1;

    function apply() {
      rows.forEach(function (row, i) { row.classList.toggle('is-active', i === active); });
      if (active > -1) fill.style.height = (((active + 1) / rows.length) * 100) + '%';
    }

    /* A thin band near the vertical center of the viewport acts as the
       "reading line." The active row is the furthest one currently
       crossing it, so scrolling back up retracts the rail the same way
       scrolling down fills it. If nothing is in the band right now
       (scrolled away entirely, above or below), the last known row
       stays active rather than resetting to zero. */
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var i = rows.indexOf(entry.target);
        if (i === -1) return;
        inBand[i] = entry.isIntersecting;
      });

      var highest = -1;
      inBand.forEach(function (on, i) { if (on) highest = i; });
      if (highest > -1) active = highest;

      apply();
    }, { threshold: 0, rootMargin: '-40% 0px -50% 0px' });

    rows.forEach(function (row) { observer.observe(row); });
  }

  /* =========================================================
     CART DRAWER: rotation upsell (delegated once, so it keeps
     working after Impact replaces the drawer's innerHTML)
     ========================================================= */
  function initCartUpsell() {
    if (window.__wbcNapCartUpsellBound) return;
    window.__wbcNapCartUpsellBound = true;

    document.addEventListener('click', function (event) {
      var btn = event.target.closest ? event.target.closest('[data-nap-cart-add]') : null;
      if (!btn) return;

      event.preventDefault();
      if (btn.getAttribute('aria-busy') === 'true') return;

      var id = parseInt(btn.dataset.napCartAdd, 10);
      if (!id) return;

      btn.setAttribute('aria-busy', 'true');
      addToCart([{ id: id, quantity: 1 }])
        .catch(function () { btn.removeAttribute('aria-busy'); });
    });

    /* colour swap inside the drawer upsell card */
    document.addEventListener('click', function (event) {
      var dot = event.target.closest ? event.target.closest('[data-nap-cart-color]') : null;
      if (!dot) return;
      event.preventDefault();

      var card = dot.closest('[data-nap-cart-card]');
      if (!card) return;
      var color = dot.dataset.napCartColor;

      card.querySelectorAll('[data-nap-cart-color]').forEach(function (d) {
        d.classList.toggle('is-on', d === dot);
      });
      var label = card.querySelector('[data-nap-cart-color-label]');
      if (label) label.textContent = color;

      var thumb = card.querySelector('[data-nap-cart-thumb]');
      var variants;
      try { variants = JSON.parse(card.querySelector('[data-nap-cart-variants]').textContent); } catch (e) { return; }

      var colorIndex = parseInt(card.dataset.napCartColorIndex, 10);
      var sizeIndex = parseInt(card.dataset.napCartSizeIndex, 10);

      var first = variants.filter(function (v) { return v.options[colorIndex] === color; })[0];
      if (thumb && first && first.image) thumb.setAttribute('src', first.image);

      card.querySelectorAll('[data-nap-cart-add]').forEach(function (pill) {
        var match = variants.filter(function (v) {
          return v.options[colorIndex] === color && String(v.options[sizeIndex]) === String(pill.dataset.napCartSize);
        })[0];
        if (match) {
          pill.dataset.napCartAdd = match.id;
          pill.setAttribute('aria-disabled', match.available ? 'false' : 'true');
          pill.disabled = !match.available;
        }
      });
    });
  }

  /* =========================================================
     LIGHTBOX: tap a product image to zoom
     Self contained on purpose. Impact ships PhotoSwipe, but it is
     wired to Impact's own product-gallery DOM (media-carousel,
     data-media-type), which this custom gallery does not use.
     Mobile gets native pinch zoom, desktop gets click to zoom.
     ========================================================= */
  function initLightbox(root) {
    var opener = root.querySelector('[data-nap-zoom-open]');
    if (!opener) return;

    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-nap-gallery-thumb]'));
    var items = thumbs.map(function (t) {
      var img = t.querySelector('img');
      return { src: (img && (img.dataset.napZoom || img.dataset.napFull || img.src)) || '', alt: (img && img.alt) || '' };
    }).filter(function (i) { return i.src; });

    if (!items.length) {
      var m = root.querySelector('[data-nap-gallery-main]');
      if (!m) return;
      items = [{ src: m.dataset.napZoom || m.src, alt: m.alt || '' }];
    }

    var box, figure, imgEl, counter, lastFocus, index = 0, zoomed = false;

    function build() {
      box = document.createElement('div');
      box.className = 'nap-lightbox';
      box.setAttribute('role', 'dialog');
      box.setAttribute('aria-modal', 'true');
      box.setAttribute('aria-label', 'Product image viewer');
      box.innerHTML =
        '<button type="button" class="nap-lightbox__close" aria-label="Close">&#10005;</button>' +
        '<button type="button" class="nap-lightbox__nav nap-lightbox__nav--prev" aria-label="Previous image">&#8249;</button>' +
        '<figure class="nap-lightbox__figure"><img alt=""></figure>' +
        '<button type="button" class="nap-lightbox__nav nap-lightbox__nav--next" aria-label="Next image">&#8250;</button>' +
        '<span class="nap-lightbox__count" aria-live="polite"></span>';

      figure = box.querySelector('.nap-lightbox__figure');
      imgEl = box.querySelector('img');
      counter = box.querySelector('.nap-lightbox__count');

      box.querySelector('.nap-lightbox__close').addEventListener('click', close);
      box.querySelector('.nap-lightbox__nav--prev').addEventListener('click', function (e) { e.stopPropagation(); show(index - 1); });
      box.querySelector('.nap-lightbox__nav--next').addEventListener('click', function (e) { e.stopPropagation(); show(index + 1); });
      box.addEventListener('click', function (e) { if (e.target === box || e.target === figure) close(); });

      /* desktop: click the image to toggle a 2x zoom centred on the pointer */
      imgEl.addEventListener('click', function (e) {
        e.stopPropagation();
        if (window.matchMedia('(hover: none)').matches) return; /* touch devices pinch instead */
        zoomed = !zoomed;
        if (zoomed) {
          var r = imgEl.getBoundingClientRect();
          imgEl.style.transformOrigin = (((e.clientX - r.left) / r.width) * 100) + '% ' + (((e.clientY - r.top) / r.height) * 100) + '%';
          imgEl.style.transform = 'scale(2)';
        } else {
          imgEl.style.transform = '';
        }
        box.classList.toggle('is-zoomed', zoomed);
      });

      if (items.length < 2) {
        box.querySelectorAll('.nap-lightbox__nav').forEach(function (b) { b.hidden = true; });
        counter.hidden = true;
      }

      document.body.appendChild(box);
    }

    function show(i) {
      index = (i + items.length) % items.length;
      zoomed = false;
      imgEl.style.transform = '';
      box.classList.remove('is-zoomed');
      imgEl.src = items[index].src;
      imgEl.alt = items[index].alt;
      counter.textContent = (index + 1) + ' / ' + items.length;
      figure.scrollTop = 0;
      figure.scrollLeft = 0;
    }

    function onKey(e) {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowLeft') show(index - 1);
      else if (e.key === 'ArrowRight') show(index + 1);
    }

    function open(i) {
      if (!box) build();
      lastFocus = document.activeElement;
      show(i);
      box.classList.add('is-open');
      document.documentElement.classList.add('nap-lightbox-open');
      document.addEventListener('keydown', onKey);
      box.querySelector('.nap-lightbox__close').focus();
    }

    function close() {
      if (!box) return;
      box.classList.remove('is-open', 'is-zoomed');
      document.documentElement.classList.remove('nap-lightbox-open');
      document.removeEventListener('keydown', onKey);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    opener.addEventListener('click', function () {
      var active = root.querySelector('[data-nap-gallery-thumb].is-on');
      var start = active ? Math.max(0, thumbs.indexOf(active)) : 0;
      open(start);
    });
  }

  ready(function () {
    document.querySelectorAll('[data-nap-buybox]').forEach(function (el) {
      initBuyBox(el);
      initLightbox(el);
    });
    document.querySelectorAll('[data-nap-timeline]').forEach(function (el) {
      initTimeline(el);
    });
    initCartUpsell();
  });
})();
